/* eslint-disable no-mixed-operators */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { StoreContext } from '../Store';
import { Container, Tab, Alert, OverlayTrigger, Popover } from 'react-bootstrap';
import LoadInfo from './LoadInfo';
import OrderForm from './OrderForm';
import Toast from '../Toast/Toast';
import ProductInfo from './ProductInfo';
import QAChecklistTab from './QAChecklistTab/QAChecklistTab';
import { QAStoreContext } from './QAChecklistTab/QAStore';
import ConditionalWrapper from '../Util/ConditionalWrapper';
import restartSVG from '../../images/restart.svg';
import ConfirmationModal from '../ConfirmationModal';
import { ErrorBoundary } from 'react-error-boundary';
import NewloadError from '../Errors/NewloadError';
import LoadConfirmation from './LoadConfirmation';
import { ShowIf, useSecurity } from '../Security/security';
import { unifiedHeaderContainsErrors, isLoadStatusSubmitted } from '../../utils/unifiedLoads';
import { useSpreadsheetHandle } from '../Spreadsheet/Spreadsheet';
import {
  getChedQtyFromDocTypes,
  customsValue,
  getDutyRatesFromItems,
  convertToGbp,
  calculateVatLiability,
  calculateDutyLiability,
  getIsVatLiable,
} from './QAChecklistTab/utils';
import BPWarningModal from '../BusinessPartners/BPWarningModal';
import isoDateConverter from '../isoDateConverter';
import { useDropdownOptions } from './queryHooks/useDropdownOptions';
import { useExchangeRates } from './queryHooks/useExchangeRates';
import LoadStatus from './LoadStatus';
import { getNewUnifiedLoad, saveLoad, duplicateLoadCheck, updateDossierStatus } from '../../api/v2/load';
import { useOrderForms, useQaItemsActions } from './queryHooks/useQaItemsApis';
import { useBusinessPartner } from './queryHooks/useBusinessPartner';
import IpaffsModal from './Modals/IpaffsModal/IpaffsModal';

const GENERIC_ERROR =
  'An error occurred while saving the load. Please try again. If the problem persists, please contact IT.';

const NewLoad = (props) => {
  const spreadsheetHandle = useSpreadsheetHandle();
  const initialLoad = useRef(true);
  const navigate = useNavigate();
  const store = useContext(StoreContext);
  const qaStore = useContext(QAStoreContext);
  const [formComplete, setFormComplete] = useState(false);
  const [currentTab, setCurrentTab] = useState(null);
  const [orderFormCharge, setOrderFormCharge] = useState(false);
  const [transferid, setTransferid] = useState('');
  const [loadId, setLoadId] = useState(null);
  const [showConfirmResetModal, setShowConfirmResetModal] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [draftText, setDraftText] = useState(false);
  const [showRequiredFields, setShowRequiredFields] = useState(false);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadStatus, setLoadStatus] = useState(null);
  const [selectedForms, setSelectedForms] = useState(null);
  const { id } = useParams();
  const [isLoadSubmittedOnQATabDraft, setIsLoadSubmittedOnQATabDraft] = useState(false);
  const [newLoadDataModified, setNewLoadDataModified] = store.newLoadDataModified;
  const [isResetLoadTriggered, setIsResetLoadTriggered] = store.isResetLoadTriggered;
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [dupData, setDupData] = useState([]);
  const [itemsStepperDuplicateCheckLoading, setItemsStepperDuplicateCheckLoading] = useState(false);
  const loadStatusIsSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);
  const [showIpaffsModal, setShowIpaffsModal] = useState(false);

  const [showCm8Submission, setShowCm8Submission] = useState(false);

  const blocker = useBlocker(useCallback(() => newLoadDataModified, [newLoadDataModified]));

  const { staticDropdowns, dropdownsByDirection } = useDropdownOptions({ movementType: store.directionSelected[0] });
  const { routes, currencies, vatOptions, preferenceOptions } = staticDropdowns.data || {};
  const { exitPorts, entryPorts } = dropdownsByDirection.data || {};
  const { data: orderFormsData } = useOrderForms(id);
  const { fetchCommodityDutyRates } = useQaItemsActions();

  const { fetchExchangeRate } = useExchangeRates(currencies);

  const queryClient = useQueryClient();
  const { fetchBusinessPartnerStatus } = useBusinessPartner();

  const [isHybridPort, setIsHybridPort] = useState(false);

  const { data: newUnifiedLoadData } = useQuery({
    queryKey: ['unifiedLoad', id],
    queryFn: () => getNewUnifiedLoad(id),
    enabled: !!id,
  });

  const saveLoadMutation = useMutation({
    mutationFn: (load) => saveLoad(load),
  });

  const updateDossierStatusMutation = useMutation({
    mutationFn: (data) => updateDossierStatus(data),
  });

  useEffect(() => {
    store.qTracesChed?.[1]?.(0);
    if (!id) {
      return;
    }
    store.loadId[1](id);
    store.isEidr[1](false);
  }, [id]);

  useEffect(() => {
    if (!newUnifiedLoadData?.data) return;

    qaStore.unifiedLoadData[1](newUnifiedLoadData.data);
    const unifiedHeader = newUnifiedLoadData.data?.Headers?.[0];

    store.loadTypeDescription[1](unifiedHeader?.loadTypeDescription);
    store.loadStatus[1](unifiedHeader?.loadstatus);
    store.portalRef[1](unifiedHeader?.portalRef);
    store.jiraTicket[1](unifiedHeader?.jiraTicket || '');
    store.clientID[1](unifiedHeader?.clientID);
    store.representative[1](unifiedHeader.representative);
    store.multipleInvoices[1](unifiedHeader?.multipleInvoice || false);
    store.multipleConsignors[1](unifiedHeader?.multipleConsignor || false);

    if (unifiedHeader?.parentName) {
      store.newLoadParent[1]({
        parentId: unifiedHeader?.parentId,
        parentName: unifiedHeader?.parentName,
        parentEori: unifiedHeader?.parentEori,
      });
    }

    const apiChedQty = unifiedHeader?.qTracesChed;
    if (apiChedQty !== undefined && apiChedQty !== null) {
      store.qTracesChed?.[1]?.(Number(apiChedQty) || 0);
    }
  }, [newUnifiedLoadData?.data]);

  const { validateRole } = useSecurity();
  const showQATab = validateRole({ department: 'QA' });

  useEffect(() => {
    const clientId = store.clientID[0];
    if (!clientId) {
      store.clientIDname[1]('');
      store.clientIDEori[1]('');
      return;
    }

    let active = true;

    const fetchCustomer = async () => {
      try {
        const data = await fetchBusinessPartnerStatus(clientId);

        if (active) {
          store.clientIDname[1](data.companyName);
          store.clientIDEori[1](data.eori);
          store.billableType[1](data.billableType);
          store.isJiraTicketRequired[1](data.isJiraTicketRequired ?? false);
        }
      } catch {}
    };

    fetchCustomer();

    return () => {
      active = false;
    };
  }, [store.clientID[0]]);

  useEffect(() => {
    const { fieldsWithErrors } = unifiedHeaderContainsErrors(store, qaStore, currentTab, isHybridPort);
    setHeaderErrors(fieldsWithErrors);
  }, [store, qaStore, currentTab, isHybridPort]);

  // Fetch exchange rate when invoice currency changes
  useEffect(() => {
    const fetch = async () => {
      const exchangeRate = await fetchExchangeRate(store.invoicecurr[0], store.date[0]);
      store.exchangeRate[1](exchangeRate);
    };
    fetch();
  }, [currencies, store.invoicecurr[0], store.date[0]]);

  const prevPage = useCallback(() => {
    setCurrentTab((prev) => {
      if (prev - 1 >= 0) {
        tabs[prev].valid = true;
        setTabs((oldTabs) => {
          oldTabs[prev].valid = true;
          return [...oldTabs];
        });
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const orderFormChargeFirm = useCallback(() => {
    setOrderFormCharge(true);
    prevPage();
    prevPage();
  });

  const firstPage = useCallback(() => {
    prevPage();
    prevPage();
  });

  const lastPage = useCallback(() => {
    nextPage();
    nextPage();
  });

  const nextPage = useCallback(() => {
    setOrderFormCharge(false);
    setCurrentTab((prev) => {
      if (prev + 1 < tabs.length) {
        setTabs((oldTabs) => {
          oldTabs[prev].valid = true;
          oldTabs[prev + 1].valid = true;
          return [...oldTabs];
        });
        return prev + 1;
      }
      return prev;
    });
  }, []);

  const formatQAData = () => {
    const companyData = {
      consignee: store.QACustomerData[0]?.companyName,
      eori: store.QACustomerData[0]?.eori,
      cabieStatus: store.QACustomerData[0]?.cabieStatus,
      cabieStatusValid: store.QACustomerData[0]?.cabieStatusValid,
      dbStatus: store.QACustomerData[0]?.dbStatus,
      dbStatusValid: store.QACustomerData[0]?.dbStatusValid,
      defermentNumber: store.QACustomerData[0]?.defermentNumber,
      cashAccountNumber: store.QACustomerData[0]?.cashAccountNumber,
      standingAuthority: null,
      standingAuthorityValid: 0,
    };
    const consigneeData = {
      consignee: store.QAConsigneeData[0]?.consignee,
      eori: store.QAConsigneeData[0]?.eori,
      cabieStatus: store.QAConsigneeData[0]?.cabieStatus,
      cabieStatusValid: store.QAConsigneeData[0]?.cabieStatusValid,
      dbStatus: store.QAConsigneeData[0]?.dbStatus,
      dbStatusValid: store.QAConsigneeData[0]?.dbStatusValid,
      standingAuthority: store.QAConsigneeData[0]?.standingAuthority,
      standingAuthorityValid: store.QAConsigneeData[0]?.standingAuthorityValid,
      defermentNumber: store.QAConsigneeData[0]?.defermentNumber,
      cashAccountNumber: store.QAConsigneeData[0]?.cashAccountNumber,
    };
    const consignorData = {
      consignee: store.QAConsignorData[0]?.consignee,
      eori: store.QAConsignorData[0]?.eori,
      cabieStatus: store.QAConsignorData[0]?.cabieStatus,
      cabieStatusValid: store.QAConsignorData[0]?.cabieStatusValid,
      dbStatus: store.QAConsignorData[0]?.dbStatus,
      dbStatusValid: store.QAConsignorData[0]?.dbStatusValid,
      standingAuthority: store.QAConsignorData[0]?.standingAuthority,
      standingAuthorityValid: store.QAConsignorData[0]?.standingAuthorityValid,
      exWorks: store.QAConsignorData?.[0]?.exWorks,
      exWorksValid: store.QAConsignorData?.[0]?.exWorksValid,
    };

    return { companyData: companyData, consigneeData: consigneeData, consignorData: consignorData };
  };

  const createLoadToPost = (submitLoadAsDraftOnQATab, authLoad) => {
    const newLoad = {
      clientID: store.clientID[0],
      Declarant: store.declarant[0],
      name: store.selectName[0],
      jiraTicket: store.jiraTicket[0],
      jobref: store.jobRef[0],
      consignor: store.consignor[0],
      consignee: store.consignee[0],
      invoicenumber: store.invoicenumber[0],
      poNumber: store.poNumber[0],
      shipStatus: store.shipStatus[0],
      truck: store.truck[0],
      trailerNo: store.trailer[0],
      date: store.date[0],
      direction: store.directionSelected[0],
      countrydespatch: store.despatch[0],
      countrydestination: store.destination[0],
      exitport1: store.RowImport[0] ? null : store.exit[0],
      exitport2: store.RowImport[0] ? null : store.exit2[0],
      entryport1: store.RowExport[0] ? null : store.entry[0],
      entryport2: store.RowExport[0] ? null : store.entry2[0],
      invoicecurr: store.invoicecurr[0],
      incotermcode: store.selectincoterm[0],
      officeOfDest: store.officeOfDest[0],
      imo: store.container[0],
      id: !store.loadId[0] ? null : store.loadId[0],
      trailerSeal: store.trailerSeal[0],
      controlledGoods: store.controlledGoods[0],
      container: store.container[0],
      GBimport: store.GBimport[0],
      portsModal: store.portsModal[0],
      adjustments: store.adjustments[0],
      packageTotal: store.packageTotal[0],
      draft: store.isEidr[0] ? 1 : submitLoadAsDraftOnQATab ? 1 : 0,
      saveAsStatus: !!qaStore.saveLoadAs[0] ? qaStore.saveLoadAs[0] : null,
      incotermLocation: store.incotermLocation[0],
      fromFile: store.newLoadFromFile[0] ? true : false,
      transitMRN: store.transitMRN[0],
      representative: store.representative[0],
      certificateCount: store.certificateCount[0],
      orderFormId: store.orderFormId[0],
      customsMoPConfirmation: store.customsMoPConfirmation[0],
      customsMop: store.customsMopValue[0],
      traderParent: store.newLoadParent?.[0]?.['parentId'] || null,
      authLoad: authLoad,
      multipleConsignor: store.multipleConsignors[0],
      multipleInvoice: store.multipleInvoices[0],
      totalItemCustomsValue: store.totalItemCustomsValue[0],
      docId: store.processedRpaId[0], //if we have processed an rpa file from the documents table
      docStatus: store.processedRpaId[0] ? 'RPA Processed' : null,
      importFile: store.importFileData[0] || null,
    };

    const { companyData, consigneeData, consignorData } = formatQAData();

    newLoad['QAPreferenceData'] = store.preferenceData[0];
    newLoad['QACompanyData'] = companyData;
    newLoad['QAConsigneeData'] = consigneeData;
    newLoad['QAConsignorData'] = consignorData;

    setDraftText(store.isEidr[0]);

    const ipaffsModalData = store.ipaffsCategories[0];

    newLoad.items = store.validatedRowsReadyToSubmit[0].map((row, i) => {
      if (isNaN(row.VAT)) {
        row.VAT = vatOptions?.find((opt) => opt.data === row.VAT?.trim()?.toUpperCase())?.id;
      }
      if (isNaN(row.preferential)) {
        row.preferential = preferenceOptions?.find(
          (opt) => opt?.data?.trim() === row.preferential?.trim()?.toUpperCase()
        )?.id;
      }

      row.pref = row.preferential;

      const itemIpaffsData = ipaffsModalData?.[i + 1];
      if (itemIpaffsData) {
        row.riskCategory = itemIpaffsData.riskCategory;
        row.consolidatedIpaffs = itemIpaffsData.consolidatedIpaffs;
        row.ipaffsHSCode = itemIpaffsData.ipaffsHSCode;
        row.riskCategoryName = itemIpaffsData.riskCategoryName;
      }

      if (store.QAProductsList[0]?.[i]) {
        row.dutyLiability = store.QAProductsList[0][i]['dutyLiability'];
        row.vatLiability = store.QAProductsList[0][i]['vatLiability'];
      }

      return row;
    });
    newLoad.qTracesChed = getChedQtyFromDocTypes(store.docTypes[0]);
    newLoad.docTypes = store.docTypes[0]; //.filter(document => document.checked === true); // if we need to remove fields : .map(document => ({...document}));

    return newLoad;
  };

  // Gets the column index for a given column name from the spreadsheet handle
  const getColumnIndex = (columnName) => {
    const keys = spreadsheetHandle.current.excelState.config?.map((c) => c.key);
    if (!keys) {
      return -1;
    }
    const columnIndex = keys.findIndex((e) => e === columnName);
    return columnIndex;
  };

  // Memoized computation of sheet items for the Check IPAFFs modal
  const sheetItems = useMemo(() => {
    return spreadsheetHandle?.current?.excelState?.rows
      ?.map((row, index) => {
        const hsCodeIndex = getColumnIndex('commoditycode');
        const healthCertIndex = getColumnIndex('healthCert');
        const grossWeightIndex = getColumnIndex('grossMass');
        const originIndex = getColumnIndex('origin');
        const descriptionIndex = getColumnIndex('description');

        const itemId = index;
        const hsCode = row[hsCodeIndex]?.text;
        const healthCert = row[healthCertIndex]?.text;
        const grossWeight = row[grossWeightIndex]?.text || 0;
        const origin = row[originIndex]?.text;
        const description = row[descriptionIndex]?.text;

        // If hsCode is not valid, return undefined
        if (!hsCode) {
          return undefined;
        }
        return { itemId, hsCode, healthCert, grossWeight, origin, description };
      })
      .filter((item) => item !== undefined); // Filter out undefined values
  }, [spreadsheetHandle.current.excelState.rows]);

  // update the spreadsheet with the new values for the healthCert column (from IPAFFs modal)
  const updateSpreadsheetIpaffsData = async (values) => {
    const updatedRows = [...spreadsheetHandle.current.excelState.rows];
    const healthCertIndex = getColumnIndex('healthCert');

    values.forEach((value) => {
      const itemId = value.itemId;
      const newValue = value.consolidatedIpaffs || value.riskCategoryText;
      if (updatedRows[itemId] && newValue && newValue !== 'N/A') {
        updatedRows[itemId][healthCertIndex].text = newValue;
      }
    });

    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: updatedRows,
    });
  };

  const updateDutyVat = async (load) => {
    const vatAdjustment = Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.amount || 0);
    const vatCurrency = await fetchExchangeRate(
      currencies,
      Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const vatAdjustmentGbp = Number(convertToGbp(vatAdjustment, vatCurrency));
    const dutyRatesUtils = await getDutyRatesFromItems(
      load.items,
      store.exchangeRate[0],
      vatAdjustmentGbp,
      store.RoiImport[0],
      store.ffd[0] || store.GBimport[0],
      fetchCommodityDutyRates
    );
    const updatedItems = load.items.map((row, i) => {
      row.dutyLiability = calculateDutyLiability(dutyRatesUtils.data, i, row.dutyLiability);
      row.vatLiability = calculateVatLiability(dutyRatesUtils.data, i, getIsVatLiable(row.VAT), row.vatLiability);
      return row;
    });
    load.items = updatedItems;
    const updatedLoad = load;
    return updatedLoad;
  };

  const updateCustomsValue = async (load) => {
    const freightCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.amount || 0);
    const insuranceCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.amount || 0);
    const otherCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.amount || 0);
    const invoiceDiscount = Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.amount || 0);
    const freightCurrency = await fetchExchangeRate(
      currencies,
      Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const insuranceCurrency = await fetchExchangeRate(
      currencies,
      Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const otherCurrency = await fetchExchangeRate(
      currencies,
      Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const invoiceCurrency = await fetchExchangeRate(
      currencies,
      Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    store.totalItemCustomsValue[1](
      customsValue(
        store.totalItemValue[0],
        store.exchangeRate[0],
        freightCharges,
        insuranceCharges,
        otherCharges,
        invoiceDiscount,
        freightCurrency,
        insuranceCurrency,
        otherCurrency,
        invoiceCurrency,
        store.totalItemValue[0]
      )
    );

    if (store.ffd[0] || store.GBimport[0]) {
      const updatedItems = load.items.map((row, i) => {
        row.customsValue = customsValue(
          row.itemValue,
          store.exchangeRate[0],
          freightCharges,
          insuranceCharges,
          otherCharges,
          invoiceDiscount,
          freightCurrency,
          insuranceCurrency,
          otherCurrency,
          invoiceCurrency,
          store.totalItemValue[0]
        );

        return row;
      });
      load.items = updatedItems;
    }
    load.totalItemCustomsValue = store.totalItemCustomsValue[0];
    const updatedLoad = load;
    return updatedLoad;
  };

  const [saveError, setSaveError] = useState(null);

  const handleSubmit = async (event, isDraft, submitLoadAsDraftOnQATab, submittedThroughAuth = false) => {
    event.preventDefault();

    setIsLoadSubmittedOnQATabDraft(submitLoadAsDraftOnQATab);
    const newLoad = createLoadToPost(submitLoadAsDraftOnQATab, submittedThroughAuth);
    try {
      let finalLoad = newLoad;

      if (!showQATab) {
        // calculates customsValue, dutyLiability and vatLiability for the load and items
        // QA Tab already calculates these values - here we are calculating them if user is non-QA
        const loadWithCustomsValue = await updateCustomsValue(finalLoad);
        const loadWithDutyVat = await updateDutyVat(loadWithCustomsValue);
        finalLoad = loadWithDutyVat;
      }

      // Sending currency code to backend as it's required for ThymeIT Import submission (CABS-3698)
      const currencyCode = currencies.find((c) => c.id === store.invoicecurr[0]?.toString())?.data;
      if (currencyCode) {
        finalLoad.currencyCode = currencyCode;
      }

      const { data, fileUploadSuccess } = await saveLoadMutation.mutateAsync(finalLoad);

      if (data[0].completed === 'Inserted') {
        setNewLoadDataModified(false);
        //if data is saved, then remove the local storage data
        store.saved[1](data);
        store.docTypes[1]([]);
        store.loadId[1](data[0].id);
        store.portalRef[1](data[0].transferid); // ensure ipaffs modal has correct portal reference after saving load
        store.newLoadItemsModified[1](false); // ensure ipaffs modal download buttons are enabled after saving load
        setLoadId(data[0].id);
        setTransferid(data[0].transferid);
        //from data, get the value of 'isCm8' and if isCm8 == true AND isDraft is false, then make the call to the CM8 API to set the job to 'Done'
        if (data[0].isCm8 && !isDraft) {
          const formData = new FormData();
          formData.append('transferId', data[0].transferid);
          await updateDossierStatusMutation.mutateAsync(formData);
        }
        store.automaticRpaUploadFailed[1](fileUploadSuccess === false);
        setCurrentTab(showQATab ? 4 : 3) || setFormComplete(true);
      }

      // soft error - while the load is saved successfully, there may still be an error submitting to ThymeIT
      if (data[0].error) {
        setSaveError({
          message: data[0].error,
        });
      }
    } catch (err) {
      console.error('Save Error:', err);

      try {
        const error = JSON.parse(err.message);
        setSaveError(error);
      } catch (parseError) {
        console.error('Parse Error:', parseError);
        setSaveError({
          message: GENERIC_ERROR,
        });
      }
    }
  };

  //Set the overlay information for the Header tab
  useEffect(() => {
    setTabs((prev) => {
      prev[0].overlay = (
        <Popover content>
          <div className="gap-2 bg-white d-flex flex-column">
            <div className="d-flex flex-column">
              <div
                style={{
                  fontWeight: '500',
                  color: 'hsl(0 0% 50%)',
                  fontSize: '90%',
                }}
              >
                Route
              </div>
              <div>
                {routes?.find((option) => option?.id?.toString() === store?.directionSelected?.[0]?.toString())?.data}
              </div>
            </div>
            <div className="d-flex flex-column">
              <div
                style={{
                  fontWeight: '500',
                  color: 'hsl(0 0% 50%)',
                  fontSize: '90%',
                }}
              >
                Export Port 1
              </div>
              <div>{exitPorts?.find((option) => option?.id?.toString() === store?.exit?.[0]?.toString())?.data}</div>
            </div>
            <div className="d-flex flex-column">
              <div
                style={{
                  fontWeight: '500',
                  color: 'hsl(0 0% 50%)',
                  fontSize: '90%',
                }}
              >
                Entry Port 1
              </div>
              <div>{entryPorts?.find((option) => option?.id?.toString() === store?.entry?.[0]?.toString())?.data}</div>
            </div>
          </div>
        </Popover>
      );
      return [...prev];
    });
  }, [store.directionSelected[0], store.exit[0], store.entry[0]]);

  //Set the overlay information for the Items tab
  useEffect(() => {
    const countObj = store.validatedRowsReadyToSubmit[0].reduce((acc, curr) => {
      const notAHash = `${curr.commoditycode}|${curr.origin}`;
      if (acc[notAHash] !== undefined) {
        acc[notAHash] = acc[notAHash] + 1;
      } else {
        acc[notAHash] = 1;
      }
      return acc;
    }, {});

    setTabs((prev) => {
      prev[1].overlay = (
        <Popover content>
          <div className="gap-2 bg-white d-flex flex-column">
            <div className="d-flex flex-column">
              <div
                style={{
                  fontWeight: '500',
                  color: 'hsl(0 0% 50%)',
                  fontSize: '90%',
                }}
              >
                Product Lines
              </div>
              {Object.entries(countObj).map(([key, value]) => (
                <div key={key}>
                  {key.split('|').join(' ').toUpperCase()} - {value}x
                </div>
              ))}
            </div>
          </div>
        </Popover>
      );
      return [...prev];
    });
  }, [store.validatedRowsReadyToSubmit[0]]);

  //Set the overlay information for the Order Form tab
  useEffect(() => {
    setTabs((prev) => {
      prev[2].overlay = (
        <Popover content>
          <div className="gap-2 bg-white d-flex flex-column">
            <div className="d-flex flex-column">
              <div
                style={{
                  fontWeight: '500',
                  color: 'hsl(0 0% 50%)',
                  fontSize: '90%',
                }}
              >
                Selected Documents
              </div>
              <ul
                style={{
                  fontSize: '90%',
                  marginLeft: '1rem',
                  paddingLeft: '0',
                }}
              >
                {store.docTypes[0]
                  .filter((doc) => doc.checked)
                  .map((doc, index) => (
                    <li key={index}>{doc.title}</li>
                  ))}
              </ul>
            </div>
          </div>
        </Popover>
      );
      return [...prev];
    });
  }, [store.docTypes[0]]);

  const tabsLinksStandard = [
    { title: 'Header' },
    { title: 'Items' },
    { title: 'Order Form' },
    { title: 'Load Confirmation' },
  ];

  const tabsLinksWithQACheck = [
    { title: 'Header' },
    { title: 'Items' },
    { title: 'Order Form' },
    { title: 'QA' },
    { title: 'Load Confirmation' },
  ];

  const [tabs, setTabs] = useState(showQATab ? tabsLinksWithQACheck : tabsLinksStandard);

  const getTabs = useMemo(() => {
    if (loadStatusIsSubmitted) {
      return tabs.map((tab) => (tab.title === 'Load Confirmation' ? { ...tab, title: 'Status' } : tab));
    }
    return tabs;
  }, [loadStatusIsSubmitted, tabs]);

  useEffect(() => {
    if (initialLoad.current === false) {
      invalidateTabs(0);
    }
  }, [store.tadEns[0]]);

  useEffect(() => {
    if (loadStatus !== null && selectedForms !== null && !store.processedRpaId[0]) {
      //default behaviour for DRAFT loads; this code is used only by DRAFT loads; 'Completed' or 'In progress' loads use src\components\LoadList\Loads
      setCurrentTab(0);
      const anyOrderFormsSelected = selectedForms.length > 0;
      //specific scenarios - IF logged user has QA role, and DRAFT load has any 'Order Form' documents selected then redirect user to the 'QA' tab
      if (loadStatusIsSubmitted) {
        setCurrentTab(4);
      } else if (showQATab && anyOrderFormsSelected) {
        setCurrentTab(3);
      }
    }
  }, [loadStatus, selectedForms]);

  const invalidateTabs = (after) => {
    setTabs((oldTabs) => {
      return [...oldTabs].map((tab, idx) => ({
        ...tab,
        valid: idx < after ? tab.valid : false,
      }));
    });
  };

  const handleSubmitAnotherLoad = useCallback(() => {
    setResetTrigger(true);
    store.resetNewLoad();
    qaStore.resetQAStore();
    setTimeout(() => {
      navigate('/load/', { replace: true });
      setCurrentTab(0);
      setFormComplete(false);
      initialLoad.current = false;
      invalidateTabs(0);
    }, 0);
  }, []);

  const handleResetPage = useCallback(() => {
    store.resetNewLoad();
    qaStore.resetQAStore();
    setCurrentTab(0);
    window.location.reload();
  }, [store]);

  useEffect(() => {
    initialLoad.current = false;
    store.pasteNewLoad[1](true);
    if (!id) {
      setCurrentTab(0);
    }
  }, []);

  useEffect(() => {
    if (!id && !orderFormsData) {
      setSelectedForms(null);
      return;
    }
    if (!id || !orderFormsData) return;
    const orderforms = orderFormsData?.[0]?.orderforms && JSON.parse(orderFormsData?.[0]?.orderforms);
    setSelectedForms(orderforms ?? []);
  }, [id, orderFormsData]);

  useEffect(() => {
    // checking if user is trying to reset a load
    if (isResetLoadTriggered && !id) {
      spreadsheetHandle.current.excelDispatch({ type: 'reset' });
      setCurrentTab(0);
      setIsResetLoadTriggered(false);
      qaStore.resetQAStore();
    }
  }, [isResetLoadTriggered, id]);

  //function to check if the button should be disabled or not. Added for CABS-1123
  function isButtonDisabled(idx, tabValid, formComplete) {
    if (itemsStepperDuplicateCheckLoading && idx === 1) {
      return true;
    }
    const jobRefTooLong = (store.jobRef[0]?.length ?? 0) > 35;
    if (idx === 1 && jobRefTooLong) {
      return currentTab !== 1;
    }
    //if idx is 1 and !store.clientID[0] and the form is not completed, then return false. Also have the same check for newLoadFromFile, so we can enable the second button if the user has uploaded a RPA file.
    if (idx === 1 && store.clientID[0] !== null && !formComplete) return false;
    return (currentTab < idx && !tabValid) || formComplete;
  }

  //function to set the tabs other than the first 2 invalid.
  const setTabsInvalid = () => {
    setTabs((prev) => {
      return prev.map((item, index) => {
        return index > 1 ? { ...item, valid: false } : item;
      });
    });
  };

  /**
   * @returns {Promise<boolean>} true when duplicates were found (modal may be shown), or when the API call fails — caller should not navigate; false when no duplicate.
   */
  async function checkForDuplicateLoad() {
    const payload = {
      consignor: store?.consignor[0],
      consignee: store?.consignee[0],
      invoicenum: store?.invoicenumber[0],
      draftId: store?.loadId[0],
      jobRef: store.jobRef[0],
      multipleConsignor: store.multipleConsignors[0],
      multipleInvoice: store.multipleInvoices[0],
    };
    setItemsStepperDuplicateCheckLoading(true);
    try {
      const duplicateData = await queryClient.fetchQuery({
        queryKey: ['duplicateCheck', payload],
        queryFn: () => duplicateLoadCheck(payload),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      const isDuplicateFound = !!duplicateData[0]?.duplicate;

      if (isDuplicateFound) {
        setDupData(duplicateData);
        setShowDuplicateModal(true);
        return true;
      }
      return false;
    } catch {
      /* keep user on Header if duplicate check fails */
      return true;
    } finally {
      setItemsStepperDuplicateCheckLoading(false);
    }
  }

  const handleDuplicateModalConfirm = () => {
    setShowDuplicateModal(false);
    nextPage();
  };

  //function to handle changing tabs. Added for CABS-1123
  async function handleChangingTab(idx) {
    const isLoadSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);
    if (isLoadSubmitted) {
      setCurrentTab(idx);
      return;
    }
    //check if currentTab === 0 and idx === 1 and store.clientID[0] and store.directionSelected[0] and store.invoicenumber[0] and store.jobRef[0]
    if (currentTab === 0 && idx === 1 && store.clientID[0] !== null) {
      const validTruckNum = store?.truck[0] ? store.truck[0].length <= 35 : true;
      const validContainer = store?.container[0] ? store.container[0].length <= 17 : true;

      if (store.directionSelected[0] && (store?.invoicenumber[0] || store.multipleInvoices[0]) && store.jobRef[0]) {
        setShowRequiredFields(false);
        const validJobRefLen = (store.jobRef[0]?.length ?? 0) <= 35;
        const invoiceFieldLengthOk = store.multipleInvoices[0] || (store.invoicenumber[0]?.length ?? 0) <= 35;
        if (invoiceFieldLengthOk && validTruckNum && validContainer && validJobRefLen) {
          const isDuplicateLoad = await checkForDuplicateLoad();
          if (!isDuplicateLoad) {
            setCurrentTab(idx);
          }
        }
      } else {
        setShowRequiredFields(true);

        const errorMessageText = store.multipleInvoices[0]
          ? 'To go to the next page, please fill in the Route and Job Reference fields'
          : 'To go to the next page, please fill in the Route, Job Reference and Invoice Number fields';
        setErrorMessage(errorMessageText);
        setAlertOpen(true);
      }
    } else if (idx < 2 && currentTab > 1) {
      setTabsInvalid();
      setCurrentTab(idx);
    } else {
      setCurrentTab(idx);
    }
  }

  function getButtonClassName(formComplete, currentTab, idx, tab) {
    if (loadStatusIsSubmitted) {
      return `tab-control text-eoriblue ${currentTab === idx ? 'active' : 'done'}`;
    }

    if (currentTab === 0 && idx === 1) {
      return 'tab-control text-eoriblue done';
    } else if (formComplete || currentTab > idx || (tab.valid && currentTab < idx)) {
      return 'tab-control text-eoriblue done';
    } else if (currentTab === idx) {
      return 'tab-control text-eoriblue active';
    }
    return 'tab-control text-eoriblue neutral';
  }

  const handleTableClicked = useCallback(() => {
    if (isAlertOpen) {
      setAlertOpen(false);
    }
  }, [isAlertOpen]);

  const [headerErrors, setHeaderErrors] = useState([]);

  const getTextForDraftScreen = () => {
    if (showCm8Submission) {
      return {
        title: `Submitted to CM8`,
        body: `has been submitted to CM8`,
      };
    } else if (isLoadSubmittedOnQATabDraft || draftText) {
      return {
        title: `Saved as ${qaStore.saveLoadAs[0]}`,
        body: `saved as ${qaStore.saveLoadAs[0]}`,
      };
    } else {
      return {
        title: 'Submitted',
        body: 'submitted',
      };
    }
  };

  const submitToCustomaite = async () => {
    setShowCm8Submission(true);
    setNewLoadDataModified(false);
    setCurrentTab(showQATab ? 4 : 3);
  };

  const loadConfirmationTabIndex = showQATab ? 4 : 3;
  const loadConfirmationScreenVisible = currentTab === loadConfirmationTabIndex;

  return (
    <ErrorBoundary
      FallbackComponent={NewloadError}
      onReset={() => {
        handleResetPage();
      }}
    >
      <Container fluid id="breadcrumb-tabber" className="pt-4 mt-3" onClick={handleTableClicked}>
        <Toast className="error-toast position-fixed">
          <Alert style={{ minWidth: '500px', maxWidth: '35rem', backgroundColor: '#E4E3ED' }} show={isAlertOpen}>
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 p-3"
              onClick={() => setAlertOpen(false)}
            />
            <div className="floating-box-header-newload align-items-center" style={{ margin: 0, padding: 0 }}>
              <span>Please fill all the required fields</span>
            </div>
            <p style={{ fontSize: '100%', fontWeight: 500 }} className="text-danger text-center">
              {errorMessage}
            </p>
          </Alert>
        </Toast>
        <div className="floating-back-button d-none d-lg-block">
          {currentTab > 0 && !formComplete && (
            <button
              className="blue-button-newload"
              style={{
                borderRadius: '0px 0.75rem 0.75rem 0px',
                padding: '0.25rem',
                width: '2.5rem',
              }}
              onClick={prevPage}
            >
              <LeftChevron />
            </button>
          )}
        </div>
        <Container fluid className="">
          <BPWarningModal
            show={blocker.state === 'blocked'}
            onConfirm={() => {
              store.resetNewLoad();
              blocker.proceed();
            }}
            onHide={() => blocker.reset()}
          />
          <ConfirmationModal
            header="Duplicates detected"
            show={showDuplicateModal}
            onHide={() => setShowDuplicateModal(false)}
            onConfirm={handleDuplicateModalConfirm}
            hideCancel={false}
            animation={false}
            centered
          >
            <p>Duplicate combination of the following has been detected.</p>
            {store.multipleInvoices[0] ? (
              <ul>
                <li>Consignee code</li>
                {!store.multipleConsignors[0] && <li>Consignor code</li>}
                <li>Job Reference</li>
              </ul>
            ) : (
              <ul>
                <li>Consignee code</li>
                <li>Consignor code</li>
                <li>Invoice number</li>
              </ul>
            )}
            <p>Duplicate load details:</p>
            <ul>
              {dupData?.map((duplication) => (
                <li key={duplication?.portalRef}>
                  <span>{duplication?.portalRef}</span> -{' '}
                  <span>{isoDateConverter(duplication?.loadDate, true, false, true)}</span>{' '}
                  <span>{duplication?.jobStatus}</span>
                </li>
              ))}
            </ul>
            <p>Proceeding to the next step may result in duplication.</p>
          </ConfirmationModal>
          <Tab.Container activeKey={currentTab}>
            <Container fluid="lg" className="mb-4 semi-fluid-container">
              <div className="tab-header w-100">
                {getTabs.map((tab, idx) => {
                  const condition = showQATab
                    ? tab.overlay && currentTab > idx
                    : tab.overlay && currentTab > idx && !formComplete;

                  return (
                    <React.Fragment key={idx}>
                      <ConditionalWrapper
                        condition={condition}
                        wrapper={(children) => (
                          <OverlayTrigger placement="bottom" trigger={['hover', 'focus']} overlay={tab.overlay}>
                            {children}
                          </OverlayTrigger>
                        )}
                      >
                        <button
                          className={getButtonClassName(formComplete, currentTab, idx, tab)}
                          disabled={!loadStatusIsSubmitted && isButtonDisabled(idx, tab.valid, formComplete, tab.title)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChangingTab(idx);
                          }}
                        >
                          <NumberedCircle i={idx} done={formComplete} />
                          <span>{tab.title}</span>
                        </button>
                      </ConditionalWrapper>
                      {idx !== getTabs.length - 1 && (
                        <div
                          className={`horizontal-line flex-grow-1 rounded-pill ${
                            currentTab > idx ? 'bg-success' : 'bg-gray'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
                <div class="flex-grow-1" />
                <button className="reset-btn" onClick={() => setShowConfirmResetModal(true)}>
                  <span>Reset Load</span>
                  <img style={{ color: 'blue' }} src={restartSVG} width="20" alt="" />
                </button>
              </div>
            </Container>

            <Tab.Content>
              {/* Step 1 - Header */}
              <Tab.Pane eventKey={0}>
                <LoadInfo
                  nextPage={nextPage}
                  checkForDuplicateLoad={checkForDuplicateLoad}
                  duplicateCheckLoading={itemsStepperDuplicateCheckLoading}
                  props={props}
                  showRequiredFields={showRequiredFields}
                  updateLoadStatus={setLoadStatus}
                  id={id}
                  spreadsheetHandle={spreadsheetHandle}
                  orderFormCharge={orderFormCharge}
                  headerErrors={headerErrors}
                  setAlertOpen={setAlertOpen}
                  setErrorMessage={setErrorMessage}
                  setShowCm8Submission={submitToCustomaite}
                  isHybridPort={isHybridPort}
                  setIsHybridPort={setIsHybridPort}
                />
              </Tab.Pane>

              {/* Step 2 - Items */}
              <Tab.Pane eventKey={1}>
                <ProductInfo
                  visible={currentTab === 1}
                  nextPage={nextPage}
                  lastPage={lastPage}
                  submit={(e) => handleSubmit(e, true, false)}
                  resetTrigger={resetTrigger}
                  setResetDone={() => setResetTrigger(false)}
                  prevPage={prevPage}
                  loadStatus={loadStatus}
                  showQATab={showQATab}
                  hasErrors={!!headerErrors?.length}
                  spreadsheetHandle={spreadsheetHandle}
                  eidrData={qaStore.unifiedLoadData[0]}
                  setShowIpaffsModal={setShowIpaffsModal}
                  getColumnIndex={getColumnIndex}
                />
              </Tab.Pane>

              {/* Step 3 - Order Form */}
              <Tab.Pane eventKey={2}>
                <OrderForm
                  visible={currentTab === 2}
                  firstPage={firstPage}
                  nextPage={nextPage}
                  submit={(e) => handleSubmit(e, false, false)}
                  showQATab={showQATab}
                  createLoadToPost={createLoadToPost}
                  selectedForms={selectedForms}
                  setSelectedForms={setSelectedForms}
                  currentId={id}
                  setCurrentTab={orderFormChargeFirm}
                />
              </Tab.Pane>

              {/* Step 4 - QA */}
              <ShowIf or={[{ department: 'QA' }]}>
                <Tab.Pane eventKey={3}>
                  <QAChecklistTab
                    visible={currentTab === 3}
                    submitLoadAsDraft={(e) => handleSubmit(e, true, true)}
                    submitLoad={(e, authed) => handleSubmit(e, false, false, authed)}
                    headerErrors={headerErrors}
                    selectedForms={selectedForms}
                    spreadsheetHandle={spreadsheetHandle}
                    readOnly={loadStatusIsSubmitted}
                    loadId={id}
                  />
                </Tab.Pane>
              </ShowIf>

              {/* Step 5 - Load Confirmation */}
              <Tab.Pane eventKey={showQATab ? 4 : 3}>
                {!loadStatusIsSubmitted ? (
                  <LoadConfirmation
                    visible={currentTab === (showQATab ? 4 : 3)}
                    newLoadAction={handleSubmitAnotherLoad}
                    listLoadAction={() =>
                      qaStore.unifiedLoadData[0]?.Headers?.[0].portalRef[0] === 'P' || transferid[0] === 'P'
                        ? navigate('/cabie/loads')
                        : navigate('/epo/loads')
                    }
                    statusAction={() => {
                      store.resetNewLoad();
                      qaStore.resetQAStore();
                      window.location.href = `/load/${loadId || id}`;
                    }}
                    titleText={getTextForDraftScreen().title}
                    bodyText={getTextForDraftScreen().body}
                    portalRef={transferid || qaStore.unifiedLoadData[0]?.Headers?.[0].portalRef}
                    billableType={store.billableType[0]}
                    isSubmitted={!isLoadSubmittedOnQATabDraft && !draftText}
                    products={store.validatedRowsReadyToSubmit[0]}
                    setShowIpaffsModal={setShowIpaffsModal}
                  />
                ) : (
                  <LoadStatus
                    isReadOnly={false}
                    headerData={qaStore.unifiedLoadData?.[0]?.Headers?.[0]}
                    invoicenumber={id}
                    portalRef={qaStore.unifiedLoadData?.[0]?.Headers?.[0]?.portalRef}
                    prevPage={() => {
                      setCurrentTab((prev) => prev - 1);
                    }}
                  />
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </Container>

      <ConfirmationModal
        header="Confirm Reset"
        show={showConfirmResetModal}
        onHide={() => setShowConfirmResetModal(false)}
        onConfirm={handleResetPage}
      >
        Are you sure you want to reset this load? All current progress will be lost.
      </ConfirmationModal>

      <ConfirmationModal header="An error occurred" show={!!saveError} onHide={() => setSaveError(null)} hideConfirm>
        {saveError?.error || saveError?.message || GENERIC_ERROR}
      </ConfirmationModal>

      <IpaffsModal
        show={showIpaffsModal}
        onHide={() => setShowIpaffsModal(false)}
        onConfirm={(values, data) => updateSpreadsheetIpaffsData(values, data)}
        itemsRows={sheetItems}
        ipaffsCategories={store.ipaffsCategories[0]}
        setIpaffsCategories={store.ipaffsCategories[1]}
        certificateCount={store.certificateCount[0]}
        setCertificateCount={store.certificateCount[1]}
        isReadOnly={loadConfirmationScreenVisible}
      />
    </ErrorBoundary>
  );
};

// these need to be moved to an asset file
const NumberedCircle = ({ i, bg, fg, done }) => (
  <span className={`icon-md ${bg} ${fg} rounded-circle`}>
    {done ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon-sm" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      i + 1
    )}
  </span>
);

const LeftChevron = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="icon-lg" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export default NewLoad;
